// classificationGutterPlugin.js
// Plugin to add visual classification level gutters to paragraphs and headings

import {Plugin} from 'prosemirror-state';
import {Decoration, DecorationSet} from 'prosemirror-view';
import {setNodeClassificationLevel, setNodeClassificationMetadata, setNodeManualClassificationBaseline} from '../helpers/classificationHelpers';
import {getClassificationByValue} from '../helpers/classificationLevels';
import {getBlockCitationsMaxLevel, getMaxClassificationLevel} from '../helpers/documentClassification';
import {syncDocumentHeaderClassification} from '../helpers/classificationHeaderSync';
import {blockClassificationLevel, blockClassificationTokens} from '../../../utils/documentHeaderMeta';
import useYDocStore from '../../../stores/useYDocStore';

/**
 * Format classification string with distribution and compartment
 * Format: classificationLevel//distribution1/distribution2//compartment1/compartment2
 * Example: U//FOUO/NF
 */
function formatClassificationString(classLevel, distribution = [], compartment = []) {
    let result = classLevel;

    // Add distribution if present
    if (distribution && distribution.length > 0) {
        result += '//' + distribution.join('/');
    }

    // Add compartment if present
    if (compartment && compartment.length > 0) {
        if (distribution && distribution.length > 0) {
            result += '//' + compartment.join('/');
        } else {
            // If no distribution but has compartment, still use // separator
            result += '//' + compartment.join('/');
        }
    }

    return result;
}

function hasInProgressAiProcessing(aiProcessing) {
    if (!aiProcessing || typeof aiProcessing !== 'object') return false;
    return Object.values(aiProcessing).some((status) => status === 'inProgress');
}

function isClickOnClassificationBadge(event, element) {
    const rect = element.getBoundingClientRect();
    const beforeStyle = window.getComputedStyle(element, '::before');
    const height = parseFloat(beforeStyle.height) || 0;
    const width = parseFloat(beforeStyle.width) || 0;
    const paddingLeft = parseFloat(beforeStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(beforeStyle.paddingRight) || 0;
    const topOffset = parseFloat(beforeStyle.top) || 0;
    const leftOffset = parseFloat(beforeStyle.left) || 0;
    const badgeTop = rect.top + topOffset;
    const badgeBottom = badgeTop + height;
    const badgeLeft = rect.left + leftOffset;
    const badgeRight = badgeLeft + width + paddingLeft + paddingRight;
    return (
        event.clientX >= badgeLeft &&
        event.clientX <= badgeRight &&
        event.clientY >= badgeTop &&
        event.clientY <= badgeBottom
    );
}

/**
 * Plugin that adds visual classification level indicators (gutters) to paragraphs and headings
 * The gutter appears on the top of the block
 * Click on the gutter to change classification level
 * @param {Object} options - Plugin options
 * @param {Function} options.openClassificationMenu - Function to open classification menu
 * @param {string} options.documentId - Document ID for Firestore updates
 * @param {Object} options.yDocMeta - Y.Map reference for document metadata
 * @param {Object} options.ydoc - Y.Doc reference for transactions
 */
export function classificationGutterPlugin({openClassificationMenu, documentId, yDocMeta, ydoc}) {
    // Tracks the currently-open classification menu's target block position across
    // transactions (ours and remote Yjs peers'). Attr-only changes (setNodeMarkup) make
    // ProseMirror recreate that block's DOM element, so re-resolving via the originally
    // clicked DOM node (the old approach) breaks after the first apply. Remapping this
    // position through every transaction keeps it valid for as long as the menu stays open.
    let activeBlockPos = null;

    return new Plugin({
        state: {
            init(_, state) {
                return createDecorations(state.doc);
            },
            apply(tr, decorations, oldState, newState) {
                if (activeBlockPos != null && tr.docChanged) {
                    activeBlockPos = tr.mapping.map(activeBlockPos, -1);
                }
                // If no doc changes, just map the decorations
                if (!tr.docChanged) {
                    return decorations.map(tr.mapping, tr.doc);
                }
                // On doc changes, regenerate decorations (simpler than incremental updates)
                return createDecorations(newState.doc);
            }
        },
        props: {
            decorations(state) {
                return this.getState(state);
            },
            handleDOMEvents: {
                async click(view, event) {
                    const classified = event.target.closest('.classified');
                    if (!classified) return false;

                    if (!isClickOnClassificationBadge(event, classified)) {
                        return false;
                    }

                    event.preventDefault();
                    event.stopPropagation();

                    // Use the decorated node element as anchor; it's more stable than the inner badge span.
                    const anchor = classified;

                    const pos = view.posAtDOM(classified, 0);
                    // ProseMirror's own posAtDOM/posFromDOM return the documented sentinel `-1`
                    // (not `null`) when `classified` no longer maps to a live position in the
                    // current view — e.g. it was captured just before the block's own DOM got
                    // recreated by an earlier attribute-only change, or removed outright. Passing
                    // that straight into `doc.resolve()` throws "Position -1 out of range" — the
                    // exact crash reported for text blocks. Map/figure nodes never hit this at
                    // all: their badge click is handled entirely by their own NodeView, whose
                    // `stopEvent()` tells ProseMirror to skip this handler for their DOM, and
                    // whose `getPos()` self-heals to `null` (instead of a bad number) once the
                    // node is gone. Paragraph/heading have no NodeView, so this is the only place
                    // that same safety net can be added for them.
                    if (pos < 0) return false;
                    const $pos = view.state.doc.resolve(pos);
                    const nodePos = $pos.depth > 0 ? $pos.before($pos.depth) : pos;
                    const node = $pos.depth > 0 ? $pos.node($pos.depth) : view.state.doc.nodeAt(pos);

                    if (!node) return false;

                    // figure nodes handle their own badge click inside FigureNodeView
                    if (node.type.name === 'figure') return false;

                    // A block can never be classified below the highest citation attached to
                    // it — compute that floor so the menu can disable/clamp lower levels.
                    const {yReferenceMarks} = useYDocStore.getState();
                    const minLevel = getBlockCitationsMaxLevel(node, yReferenceMarks);

                    const currentValue = {
                        classificationLevel: node.attrs.classificationLevel || 'U',
                        distribution: node.attrs.distribution || [],
                        compartment: node.attrs.compartment || [],
                        minLevel,
                    };

                    if (!openClassificationMenu) {
                        console.warn('openClassificationMenu is not provided');
                        return false;
                    }

                    // Track this block's position so the apply callback below can re-resolve
                    // it via the mapped position instead of a (potentially stale) DOM lookup.
                    activeBlockPos = nodePos;

                    // Open the menu with a callback that applies changes immediately
                    openClassificationMenu(anchor, currentValue, (result) => {
                        // Re-resolve position from the tracked, transaction-mapped position (kept
                        // valid by the plugin's state.apply) rather than posAtDOM: attr-only
                        // updates recreate the block's DOM element, so the originally clicked
                        // node becomes stale after the first apply.
                        if (activeBlockPos == null) return;
                        const freshNodePos = activeBlockPos;
                        const freshNode = view.state.doc.nodeAt(freshNodePos);
                        if (!freshNode || freshNode.type.name === 'figure' || freshNode.type.name === 'map') return;

                        const {distribution, compartment} = result;
                        // Defensive floor: never allow a level below the block's citations,
                        // even if the menu somehow applied a lower value.
                        const classificationLevel = getMaxClassificationLevel(result.classificationLevel, minLevel);

                        const tr = view.state.tr;
                        setNodeClassificationLevel(tr, freshNodePos, classificationLevel);
                        setNodeClassificationMetadata(tr, freshNodePos, {distribution, compartment});
                        // Persist the baseline on the node itself (this is the one place a
                        // block's "manual baseline" — the level it would have with zero
                        // citations — is allowed to change; see referenceClassificationFloor.js,
                        // which reads this attr directly rather than any separate cache) so it
                        // survives a reload — see setNodeManualClassificationBaseline.
                        setNodeManualClassificationBaseline(tr, freshNodePos, classificationLevel);
                        view.dispatch(tr);

                        // Read the committed doc (post-dispatch) for accurate max-classification,
                        // not tr.doc which reflects the pre-dispatch proposed state.
                        const committedDoc = view.state.doc;

                        syncDocumentHeaderClassification({
                            documentId,
                            yDocMeta,
                            ydoc,
                            committedDoc,
                            appliedLevel: classificationLevel,
                        });
                    }, event);

                    return true;
                }
            }
        }
    });
}

/**
 * Create decorations for all classified paragraphs, headings, and figures
 */
function createDecorations(doc) {
    const decorations = [];

    doc.descendants((node, pos) => {
        const isTextBlock = node.type.name === 'paragraph' || node.type.name === 'heading';
        const isFigure = node.type.name === 'figure';
        const isMap = node.type.name === 'map';

        if (isTextBlock || isFigure || isMap) {
            const classLevel = blockClassificationLevel(node.attrs.classificationLevel);
            const distribution = blockClassificationTokens(node.attrs.distribution, 'distribution');
            const compartment = blockClassificationTokens(node.attrs.compartment, 'compartment');
            const info = getClassificationByValue(classLevel);
            if (info) {
                const formattedString = formatClassificationString(classLevel, distribution, compartment);

                // Empty unclassified paragraph styling (not applied to figure or map)
                const isEmpty = isTextBlock && node.content.size === 0;
                const isEmptyUnclassified = isEmpty && classLevel === 'U' && node.type.name === 'paragraph';
                let classNames = isEmptyUnclassified
                    ? `classified classified-${info.value.toLowerCase()} empty-unclassified`
                    : `classified classified-${info.value.toLowerCase()}`;

                if (isFigure) classNames += ' classified-figure';
                if (isMap) classNames += ' classified-map';

                const showProcessing = isTextBlock && hasInProgressAiProcessing(node.attrs.aiProcessing);

                decorations.push(
                    Decoration.node(pos, pos + node.nodeSize, {
                        class: showProcessing ? `${classNames} has-ai-processing` : classNames,
                        'data-classification-value': formattedString,
                        'data-classification-label': info.label,
                        ...(showProcessing ? {'data-ai-processing-label': 'Processing'} : {})
                    })
                );
            }
        }

        // figureCaption: badge is read-only (no click, no decoration rendered at top level)
        // so we skip it here.
    });

    return DecorationSet.create(doc, decorations);
}
